import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { Metamodel } from 'app/model/base/model';
import { AzureBatchLinkedService } from 'app/model/linkedservice/azure-batch.linkedservice';
import { format } from 'app/shared/utils/string.util';
import { supportAzureBlobType } from 'app/registries/linked-service/azure-storage-shared-fields';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { ConnectionAuthKind, CredentialTypeOptions } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { authenticationReferenceMethodOptions } from '../consts/metadata-consts';
import { azureDefaultAuthRefTypes } from '../utls/authenticationUtils';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';


const accountName: ControlMetadata = {
    type: 'textbox',
    bind: 'accountName',
    label: stringResources.ModelName_AccountName,
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true
};

const accessKey: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    bind: 'accessKey',
    label: stringResources.ModelName_AzureBatchAccessKey,
    child: {
        type: 'password',
        label: stringResources.ModelName_AzureBatchAccessKey,
        required: true,
        isLabelVertical: true
    },
    validators: [{ type: 'required', errorMessage: '' }],
    description: format(
        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureBatch_DisplayText),
        stringResources.ModelName_AzureBatchAccessKey
    )
};

const batchUri: ControlMetadata = {
    type: 'textbox',
    bind: 'batchUri',
    label: stringResources.ModelName_AzureBatchBatchUrl,
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true,
    placeholder: 'https://<accountName>.<region>.batch.azure.com'
};

const poolName: ControlMetadata = {
    type: 'textbox',
    bind: 'poolName',
    label: stringResources.ModelName_AzureBatchPoolName,
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true
};

const selectLinkedService: ControlMetadata = {
    type: 'linkedservice-select-for-sidenav',
    bind: 'azureStorageLinkedService',
    linkedServiceTypes: supportAzureBlobType,
    label: stringResources.ModelName_StorageLinkedServiceName,
    newLinkedServiceOptionText: stringResources.AddNew,
    required: true,
    validators: [{ type: 'required', errorMessage: '' }]
};

const supportMiWithCreds: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.Authentication_Reference_Method_Label,
    description: stringResources.Authentication_Reference_Method_Description,
    options: authenticationReferenceMethodOptions,
    isLabelVertical: true,
    defaultValue: '{isAuthRefMethodCredential ? "credential" : "inline"}',
    cases: [
        {
            caseValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
            children: [
                // system Assigned Managed Identity
                {
                    type: 'management-identity-info',
                    resourceType: stringResources.AzureBatch_DisplayText
                }
            ]
        },
        {
            caseValue: azureDefaultAuthRefTypes.CredentialRefTypeAuthentication,
            children: [
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.ManagedIdentity,
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                }
            ]
        }
    ]
};

const supportMiWithoutCreds: ControlMetadata = {
    // system Assigned Managed Identity
    type: 'management-identity-info',
    resourceType: stringResources.AzureBatch_DisplayText
};

const supportMi: ControlMetadata = {
    type: 'select-switch',
    required: true,
    isLabelVertical: true,
    bind: 'authenticationType',
    label: stringResources.Authentication_Method_Label,
    options: [
        {
            label: stringResources.AuthenticationType_ManagedServiceIdentity,
            value: AuthenticationType.ManagedServiceIdentity
        },
        {
            label: stringResources.AuthenticationType_AccountKey,
            value: AuthenticationType.AccountKey
        }
    ],
    defaultValue: AuthenticationType.ManagedServiceIdentity,
    cases: [
        {
            caseValue: AuthenticationType.AccountKey,
            children: [accessKey]
        },
        {
            caseValue: AuthenticationType.ManagedServiceIdentity,
            children: GlobalVariables.isSamiSupportEnabled ? [supportMiWithCreds] : [supportMiWithoutCreds]
        }
    ]
};

export const azureBatchMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: Metamodel.modelFor(AzureBatchLinkedService).name,
        displayName: stringResources.AzureBatch_DisplayText,
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'AzureBatch' as ResourceKind
        },
        supportOPDG: true,
        icon: {
            className: 'svg-i-100x100-AzureBatch'
        },
        category: ['Azure'],
        supportedAuthKindsInTrident: [
            ConnectionAuthKind.Key,
            ConnectionAuthKind.OAuth2,
            ConnectionAuthKind.ServicePrincipal,
            ConnectionAuthKind.WorkspaceIdentity
        ],
        isCompute: true,
        isFirstClass: true,
        controls: [supportMi, accountName, batchUri, poolName, selectLinkedService],

        copySinkTypes: [],
        copySourceTypes: []
    };
    return metadata;
};
